import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { API } from '@/api'
import { Button, ButtonRadio, Divider, Loading, Pagination, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView.tsx'
import { Deployment } from '@/models/deployment.ts'
import { DeploymentBill } from '@/models/deploymentBill.ts'
import AutoDeploymentTable from '@/pages/auto-deploy/components/AutoDeploymentTable.tsx'
import { TIME_UNITS_IN_MILLS } from '@/utils/time.ts'
interface FilterItem {
  label: string
  value: string
  codes: boolean
}

export function AutoDeploymentPage() {
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [autoDeploymentList, setAutoDeploymentList] = useState<Deployment[]>([])
  const [deploymentBills, setDeploymentBills] = useState<Map<string, DeploymentBill[]>>(new Map())
  const { t } = useTranslation()

  const intervalRef = useRef<number | null>(null)
  const pageSize = 10
  const statusFilters: FilterItem[] = [
    {
      label: t('deploy.status.activated'),
      value: 'Activated',
      codes: true,
    },
    {
      label: t('deploy.status.suspended'),
      value: 'Suspended',
      codes: false,
    },
  ]
  const [filter, setFilter] = useState<FilterItem | undefined>(statusFilters[0])

  const getAutoDeploymentList = useCallback(
    async (p = page, showLoading = true) => {
      if (showLoading) {
        setLoading(true)
      }

      try {
        const res = await API.getDeploymentList({
          page: p,
          per_page: pageSize,
          enabled: filter?.codes,
        })
        if (res.code === 0) {
          setPage(res.pagination.page)
          setTotal(res.pagination.total)
          const deployments = res.deployments.map((item) => new Deployment(item))
          setAutoDeploymentList(deployments)

          if (deployments.length > 0) {
            const billsMap = new Map<string, DeploymentBill[]>()
            await Promise.all(
              deployments.map(async (item) => {
                const bills = await getAutoDeploymentBilling(new Deployment(item))
                if (bills && bills.length > 0) {
                  // sort bills by [item].createTime.seconds, descending
                  bills.sort((a, b) => b.createTime.seconds - a.createTime.seconds)
                  if (bills) {
                    billsMap.set(item.resID, bills)
                  }
                }
              }),
            )
            setDeploymentBills(billsMap)
          }
        }
      } catch (error) {
        console.error(error)
      } finally {
        if (showLoading) {
          setLoading(false)
        }
      }
    },
    [page, pageSize, filter],
  )

  useEffect(() => {
    const setupInterval = () => {
      intervalRef.current = window.setInterval(() => {
        getAutoDeploymentList(page, false)
      }, 5 * TIME_UNITS_IN_MILLS.SECOND)
    }

    setupInterval()

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
      }
    }
  }, [getAutoDeploymentList, page])

  useEffect(() => {
    getAutoDeploymentList(1)
  }, [filter])

  const getAutoDeploymentBilling = useCallback(async (deployment: Deployment) => {
    if (!deployment) return []

    try {
      const res = await API.getDeploymentBill({
        res_id: deployment.resID,
        page: 1,
        per_page: 5,
      })

      if (res.code === 0 && Array.isArray(res.billing)) {
        return res.billing.map((item: any) => {
          const createTime = item.createTime?.seconds ? item.createTime : { seconds: 0, nanos: 0 }
          const finishTime = item.finishTime?.seconds ? item.finishTime : { seconds: 0, nanos: 0 }

          return new DeploymentBill({
            ...item,
            createTime,
            finishTime,
          })
        })
      }
    } catch (error) {
      console.error('Error fetching deployment bill:', error)
    }
    return []
  }, [])

  return (
    <div className="card bg-white h-full overflow-y-auto">
      <div className="flex items-start justify-between mb-4">
        <div className="h6">
          <Trans i18nKey="deploy.title">Auto Deploy</Trans>
        </div>
        <Link to="/deploy/create">
          <Button type="secondary">
            <Trans i18nKey="deploy.createBtn">New Deploy</Trans>
          </Button>
        </Link>
      </div>

      <Divider />

      <ButtonRadio
        value={filter?.value}
        items={statusFilters}
        onChange={(e) => {
          setFilter(statusFilters.find((i) => i.value === e))
        }}
      />

      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}

      {!loading && autoDeploymentList.length > 0 && (
        <div className="mt-4">
          <AutoDeploymentTable
            _deploymentList={autoDeploymentList}
            _deploymentBills={deploymentBills}
            _status={filter?.value}
            _loading={loading}
            _fetchDeploymentList={getAutoDeploymentList}
          />
        </div>
      )}
      {!loading && autoDeploymentList.length > 0 && (
        <Pagination
          size={pageSize}
          total={total}
          current={page}
          className="justify-end mt-4"
          onChange={(page) => getAutoDeploymentList(page)}
        />
      )}
      {!loading && autoDeploymentList.length === 0 && (
        <div className="mt-4">
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg" />
              </span>
            }
          />
        </div>
      )}
    </div>
  )
}
